// Figma: "Loyalty Cloud" file, node 18936:325 ("Team Members" table) — heading, search +
// Filter/Export/Invite members toolbar, selectable table with Name / Email / Status /
// Super Admin / Accounts & Roles columns, and a "N members" footer. Data comes from the
// real `getTeamMembersByAccountId` endpoint (loyaltyCloudApi/apiSlice.js); search, sort,
// selection and CSV export are client-side.
// Assumed API fields (not confirmed against the backend): `master` = Super Admin,
// `status`/`invite_status` = Pending when it contains "pending" (otherwise Active), and
// `permissions` (property sfids, built by the endpoint's transformResponse) + `role` drive
// the "<community> - <role>" chips; no permissions (or master) = "All accounts".
// Not wired yet: Filter and Invite members — the design has no panel/modal for either.
import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import {Box, ButtonBase, Checkbox, Stack, Typography} from '@mui/material';

import {tokens} from '../../themes/ThemeCustomization';
import {getAccountSfid} from '../../app/store/reducers/session';
import {getCommunities} from '../../app/store/reducers/entities';
import {useLazyGetTeamMembersByAccountIdQuery} from '../../features/loyaltyCloudApi/apiSlice';
import {useLazyGetCommunitiesByAccountSfidQuery} from '../../features/coreApi/communitiesSlice';
import {useLazyGetAccountsQuery} from '../../features/coreApi/apiSlice';
import AuthToast from '../authentication/components/AuthToast';
import InviteMembersForm from './InviteMembersForm';
import searchIcon from '../../assets/shell/table-search-icon.svg';
import filterIcon from '../../assets/shell/table-filter-icon.svg';
import exportIcon from '../../assets/shell/table-export-icon.svg';
import plusIcon from '../../assets/shell/table-plus-icon.svg';
import sortIcon from '../../assets/shell/table-sort-icon.svg';
import sortActiveIcon from '../../assets/shell/table-sort-active-icon.svg';

const COLUMNS = [
    {field: 'name', headerName: 'Name', flex: 1.6, sortable: true},
    {field: 'email', headerName: 'Email', flex: 1.6, sortable: true},
    {field: 'status', headerName: 'Status', flex: 1.1, sortable: true},
    {field: 'superAdminLabel', headerName: 'Super Admin', flex: 1.1, sortable: true},
    {field: 'accountsLabel', headerName: 'Accounts & Roles', flex: 2.4, sortable: false}
];

const CHECKBOX_COLUMN_WIDTH = 50;
const VISIBLE_CHIPS = 2;

const STATUS_STYLES = {
    Active: {backgroundColor: '#dcfce7', borderColor: '#b9f8cf', color: '#008236'},
    'Pending Invite': {backgroundColor: '#dbeafe', borderColor: '#bedbff', color: '#1447e6'}
};

const csvCell = (value) => {
    const text = String(value ?? '');
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const downloadCsv = (rows) => {
    const lines = [COLUMNS.map((col) => col.headerName).join(',')];
    rows.forEach((row) => lines.push(COLUMNS.map((col) => csvCell(row[col.field])).join(',')));
    const blob = new Blob([`﻿${lines.join('\r\n')}`], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'team-members.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const toolbarButtonSx = {
    height: 36,
    px: 1.5,
    gap: 1,
    borderRadius: '6px',
    border: `1px solid ${tokens.color.neutral200}`,
    backgroundColor: tokens.color.white
};

const checkboxSx = {
    p: 0,
    width: 16,
    height: 16,
    color: tokens.color.neutral200,
    '&.Mui-checked, &.MuiCheckbox-indeterminate': {color: tokens.color.sky600},
    '& .MuiSvgIcon-root': {fontSize: 18}
};

const Pill = ({children, sx}) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 20,
            px: 1,
            borderRadius: '999px',
            border: `1px solid ${tokens.color.neutral200}`,
            backgroundColor: tokens.color.neutral100,
            color: tokens.color.neutral950,
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '16px',
            whiteSpace: 'nowrap',
            ...sx
        }}
    >
        {children}
    </Box>
);

const MembersSettingsPage = () => {
    const sessionAccountSfid = useSelector(getAccountSfid);
    const entityCommunities = useSelector(getCommunities);
    const accountSfid = localStorage.getItem('account_sfid') || sessionAccountSfid;

    const [fetchMembers, {data, isFetching, isError}] = useLazyGetTeamMembersByAccountIdQuery();
    // Settings renders outside MainLayout, so entities.communities may be empty here —
    // fetch the account's communities and accounts directly to resolve sfids into names.
    const [fetchCommunities, {data: fetchedCommunities}] = useLazyGetCommunitiesByAccountSfidQuery();
    const [fetchAccounts, {data: fetchedAccounts}] = useLazyGetAccountsQuery();
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState({field: 'name', direction: 'asc'});
    const [selected, setSelected] = useState(() => new Set());
    // The invite form lives at /settings/members?invite=1 so the layout's back link (and the
    // browser back button) can return to the member list.
    const [searchParams, setSearchParams] = useSearchParams();
    const inviting = searchParams.get('invite') === '1';
    const setInviting = (value) => setSearchParams(value ? {invite: '1'} : {});
    const [toast, setToast] = useState(null);

    useEffect(() => {
        if (accountSfid) {
            fetchMembers(accountSfid, true);
            fetchCommunities(accountSfid, true);
        }
        fetchAccounts(undefined, true);
    }, [accountSfid, fetchMembers, fetchCommunities, fetchAccounts]);

    const knownNames = useMemo(
        () => new Map([...(fetchedAccounts || []), ...(entityCommunities || []), ...(fetchedCommunities || [])].map((c) => [c.sfid, c.name])),
        [fetchedAccounts, entityCommunities, fetchedCommunities]
    );

    // Permissions can include properties missing from the account's community list (e.g.
    // inactive ones the list endpoint filters out). They're shown generically instead of as
    // raw sfids. Don't look them up one by one: GET /api/rental_community/ needs an app-key
    // header this app doesn't send, and a 401 makes the core API client redirect to /login.
    const members = useMemo(() => {
        const nameFor = (sfid) => knownNames.get(sfid) || 'Unavailable property';
        return (data || []).map((member) => {
            const superAdmin = Boolean(member.master);
            const pending = /pending/i.test(String(member.status ?? member.invite_status ?? ''));
            const roleLabel = member.role || 'Member';
            const chips =
                superAdmin || !member.permissions?.length
                    ? ['All accounts']
                    : member.permissions.map((sfid) => `${nameFor(sfid)} - ${roleLabel}`);
            return {
                ...member,
                superAdmin,
                superAdminLabel: superAdmin ? 'Yes' : 'No',
                status: pending ? 'Pending Invite' : 'Active',
                chips,
                accountsLabel: chips.join('; ')
            };
        });
    }, [data, knownNames]);

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const filtered = members.filter(
            (member) =>
                !needle ||
                [member.name, member.email, member.status, member.accountsLabel].some((value) => String(value ?? '').toLowerCase().includes(needle))
        );
        const {field, direction} = sort;
        return [...filtered].sort((a, b) => {
            const cmp = String(a[field] ?? '').localeCompare(String(b[field] ?? ''));
            return direction === 'asc' ? cmp : -cmp;
        });
    }, [members, search, sort]);

    const rowId = (member) => member.id ?? member.email;
    const allSelected = rows.length > 0 && rows.every((member) => selected.has(rowId(member)));
    const someSelected = rows.some((member) => selected.has(rowId(member)));

    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map(rowId)));
    const toggleOne = (id) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const toggleSort = (field) =>
        setSort((prev) => ({field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'}));

    const exportRows = selected.size > 0 ? rows.filter((member) => selected.has(rowId(member))) : rows;
    const cellBorder = `1px solid ${tokens.color.neutral200}`;

    const renderCell = (member, col) => {
        switch (col.field) {
            case 'email':
                return (
                    <Typography variant="body2" fontWeight={500} noWrap sx={{color: tokens.color.sky600}}>
                        {member.email}
                    </Typography>
                );
            case 'status':
                return <Pill sx={STATUS_STYLES[member.status]}>{member.status}</Pill>;
            case 'superAdminLabel':
                return member.superAdmin ? (
                    <Box component="svg" viewBox="0 0 16 16" aria-label="Super admin" sx={{width: 16, height: 16}}>
                        <path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="#00a63e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </Box>
                ) : (
                    <Typography variant="body2" sx={{color: tokens.color.neutral300}}>
                        —
                    </Typography>
                );
            case 'accountsLabel': {
                // Design: one chip per line; the "+N" overflow chip trails the last visible one.
                const shown = member.chips.slice(0, VISIBLE_CHIPS);
                const extra = member.chips.length - shown.length;
                return (
                    <Stack alignItems="flex-start" sx={{gap: '4px', py: 0.75}}>
                        {shown.map((chip, index) => (
                            <Stack key={chip} direction="row" alignItems="center" sx={{gap: '4px'}}>
                                <Pill>{chip}</Pill>
                                {extra > 0 && index === shown.length - 1 && <Pill title={member.chips.slice(VISIBLE_CHIPS).join(', ')}>+{extra}</Pill>}
                            </Stack>
                        ))}
                    </Stack>
                );
            }
            default:
                return (
                    <Typography variant="body2" fontWeight={500} noWrap sx={{color: tokens.color.neutral950}}>
                        {member[col.field]}
                    </Typography>
                );
        }
    };

    const handleInvite = ({sent, failed}) => {
        setInviting(false);
        if (accountSfid) fetchMembers(accountSfid);
        setToast(
            failed.length === 0
                ? {
                      title: sent.length === 1 ? 'Invitation sent to 1 email address.' : `Invitations sent to ${sent.length} email addresses.`,
                      variant: 'success'
                  }
                : {title: `Invited ${sent.length}, ${failed.length} failed`, description: failed.join(' '), variant: 'error'}
        );
    };

    if (inviting) {
        const accountName = (fetchedAccounts || []).find((account) => account.sfid === accountSfid)?.name;
        return <InviteMembersForm accountSfid={accountSfid} accountName={accountName} onCancel={() => setInviting(false)} onInvite={handleInvite} />;
    }

    return (
        <>
        <AuthToast toast={toast} onClose={() => setToast(null)} />
        <Box
            sx={{
                backgroundColor: tokens.color.white,
                border: `1px solid ${tokens.color.neutral200}`,
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Stack spacing={1} sx={{pt: 2, pb: 0.5, px: 2}}>
                <Typography sx={{fontSize: '20px', fontWeight: 600, lineHeight: '28px', color: tokens.color.neutral950}}>Team Members</Typography>
                <Typography variant="body2" sx={{color: tokens.color.neutral600}}>
                    Manage your team members, roles, and access permissions.
                </Typography>
            </Stack>

            <Stack direction="row" spacing={1} sx={{p: 2, borderBottom: cellBorder}}>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        height: 36,
                        px: 1.5,
                        border: cellBorder,
                        borderRadius: '6px'
                    }}
                >
                    <Box component="img" src={searchIcon} alt="" sx={{width: 14, height: 14}} />
                    <Box
                        component="input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search members..."
                        sx={{border: 'none', outline: 'none', flex: 1, fontSize: '14px', fontFamily: 'inherit', backgroundColor: 'transparent'}}
                    />
                </Box>
                <ButtonBase sx={toolbarButtonSx}>
                    <Box component="img" src={filterIcon} alt="" sx={{width: 16, height: 16}} />
                    <Typography variant="body2" fontWeight={500}>
                        Filter
                    </Typography>
                </ButtonBase>
                <ButtonBase sx={toolbarButtonSx} onClick={() => downloadCsv(exportRows)} disabled={exportRows.length === 0}>
                    <Box component="img" src={exportIcon} alt="" sx={{width: 16, height: 16}} />
                    <Typography variant="body2" fontWeight={500}>
                        Export
                    </Typography>
                </ButtonBase>
                <ButtonBase
                    onClick={() => setInviting(true)}
                    sx={{...toolbarButtonSx, color: tokens.color.white, backgroundColor: tokens.color.sky600, borderColor: tokens.color.sky600, '&:hover': {opacity: 0.9}}}
                >
                    <Box component="img" src={plusIcon} alt="" sx={{width: 16, height: 16}} />
                    <Typography variant="body2" fontWeight={500} sx={{color: 'inherit'}}>
                        Invite members
                    </Typography>
                </ButtonBase>
            </Stack>

            <Box sx={{overflowX: 'auto'}}>
                <Box sx={{minWidth: 900}}>
                    <Stack direction="row" sx={{backgroundColor: tokens.color.neutral50}}>
                        <Stack
                            justifyContent="center"
                            sx={{width: CHECKBOX_COLUMN_WIDTH, flexShrink: 0, height: 48, px: 2, borderRight: cellBorder}}
                        >
                            <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} sx={checkboxSx} />
                        </Stack>
                        {COLUMNS.map((col, index) => {
                            const isActive = sort.field === col.field;
                            const headerSx = {
                                flex: col.flex,
                                flexBasis: 0,
                                minWidth: 100,
                                height: 48,
                                px: 2,
                                gap: 0.5,
                                justifyContent: 'space-between',
                                borderRight: index < COLUMNS.length - 1 ? cellBorder : 'none'
                            };
                            const label = (
                                <Typography variant="body2" fontWeight={500} sx={{color: isActive ? tokens.color.neutral950 : tokens.color.neutral600}}>
                                    {col.headerName}
                                </Typography>
                            );
                            if (!col.sortable) {
                                return (
                                    <Stack key={col.field} direction="row" alignItems="center" sx={headerSx}>
                                        {label}
                                    </Stack>
                                );
                            }
                            return (
                                <ButtonBase key={col.field} onClick={() => toggleSort(col.field)} sx={headerSx}>
                                    {label}
                                    <Box
                                        component="img"
                                        src={isActive ? sortActiveIcon : sortIcon}
                                        alt=""
                                        sx={{width: 14, height: 14, transform: isActive && sort.direction === 'desc' ? 'rotate(180deg)' : 'none'}}
                                    />
                                </ButtonBase>
                            );
                        })}
                    </Stack>

                    {rows.map((member) => {
                        const id = rowId(member);
                        return (
                            <Stack
                                key={id}
                                direction="row"
                                sx={{minHeight: 57, borderTop: cellBorder, backgroundColor: selected.has(id) ? tokens.color.neutral50 : tokens.color.white}}
                            >
                                <Stack justifyContent="center" sx={{width: CHECKBOX_COLUMN_WIDTH, flexShrink: 0, px: 2, borderRight: cellBorder}}>
                                    <Checkbox size="small" checked={selected.has(id)} onChange={() => toggleOne(id)} sx={checkboxSx} />
                                </Stack>
                                {COLUMNS.map((col, index) => (
                                    <Stack
                                        key={col.field}
                                        justifyContent="center"
                                        alignItems="flex-start"
                                        sx={{flex: col.flex, flexBasis: 0, minWidth: 100, px: 2, borderRight: index < COLUMNS.length - 1 ? cellBorder : 'none'}}
                                    >
                                        {renderCell(member, col)}
                                    </Stack>
                                ))}
                            </Stack>
                        );
                    })}

                    {rows.length === 0 && (
                        <Box sx={{borderTop: cellBorder, py: 4, textAlign: 'center'}}>
                            <Typography variant="body2" sx={{color: isError ? 'error.main' : tokens.color.neutral600}}>
                                {isFetching ? 'Loading members…' : isError ? 'Error fetching team members.' : 'No members found.'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Box sx={{backgroundColor: tokens.color.neutral50, borderTop: cellBorder, px: 2, py: 1.5}}>
                <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral700}}>
                    {rows.length} {rows.length === 1 ? 'member' : 'members'}
                </Typography>
            </Box>
        </Box>
        </>
    );
};

export default MembersSettingsPage;
